import type { CreateCustomAppDto, EditCustomAppDto } from "@vintecc/capture-api-types/v2";
import { apiDefaultParams } from "@/api/default.params";

import { useApiToast } from "@/hooks/apiCall";
import { queryOptions, useMutation } from "@tanstack/react-query";

export const requests = {
  key: "admin",
  getCustomApps: async (companyId: number, includeSubCompanies: boolean = false) => {
    const { default: customApps } = await import("@/api/customApp.api");

    return await customApps.v1.list(companyId, includeSubCompanies, {
      headers: {
        Authorization: apiDefaultParams.authorization,
        AuthVersion: apiDefaultParams.authVersion,
      },
    });
  },
  getCustomAppsForUser: async (includeSubCompanies: boolean = false) => {
    const { default: marketplaceApi } = await import("@/api/marketplace.api");
    return await marketplaceApi.getCustomAppsForUser(includeSubCompanies);
  },
  createCustomApp: async (data: CreateCustomAppDto) => {
    const { default: customApps } = await import("@/api/customApp.api");
    return await customApps.v2.createCustomApp(apiDefaultParams.authorization, apiDefaultParams.authVersion, data);
  },
  editCustomApp: async (id: number, data: EditCustomAppDto) => {
    const { default: customApps } = await import("@/api/customApp.api");
    return await customApps.v2.editCustomApp(id, apiDefaultParams.authorization, apiDefaultParams.authVersion, data);
  },
  deleteCustomApp: async (id: number) => {
    const { default: customApps } = await import("@/api/customApp.api");
    return await customApps.v1._delete(id, {
      headers: {
        Authorization: apiDefaultParams.authorization,
        AuthVersion: apiDefaultParams.authVersion,
      },
    });
  },
};

export const marketPlaceQueries = {
  getCustomApps: (companyId: number, includeSubCompanies: boolean) =>
    queryOptions({
      queryKey: [requests.key, companyId, includeSubCompanies],
      enabled: !!companyId,
      queryFn: () => requests.getCustomApps(companyId, includeSubCompanies),
    }),
  getCustomAppsForUser: (includeSubCompanies?: boolean) =>
    queryOptions({
      queryKey: [requests.key, "user", includeSubCompanies],
      queryFn: () => requests.getCustomAppsForUser(includeSubCompanies),
    }),
};

export const useCustomAppMutations = () => {
  const { onSuccess, onError, onSuccessCustomAppCreation } = useApiToast(requests.key);

  const createCustomApp = useMutation({
    mutationFn: requests.createCustomApp,
    onSuccess: onSuccessCustomAppCreation,
    onError,
  });

  const editCustomApp = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditCustomAppDto }) => requests.editCustomApp(id, data),
    onSuccess,
    onError,
  });

  const deleteCustomApp = useMutation({
    mutationFn: ({ id }: { id: number }) => requests.deleteCustomApp(id),
    onSuccess,
    onError,
  });

  return {
    createCustomApp,
    editCustomApp,
    deleteCustomApp,
  };
};
